import axios from "axios";
import { Config } from "../utils/config";
import { AuthActions, useAuth } from "../zustand/auth.store";

// The backend issues the refresh token as an httpOnly "jwt" cookie on
// login (see auth.controller.ts), so the browser holds it and we never see
// its value directly — we just need to send it along. Concurrent 401s (a
// page firing several requests at once) should trigger exactly one refresh
// call, not one per request, so every caller awaits the same in-flight
// promise.
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${Config.apiBaseUrl}/auth/refresh-token`,
        {},
        { withCredentials: true },
      )
      .then((res) => {
        const newToken = res.data?.accessToken;
        if (!newToken) {
          throw new Error("Refresh response had no access token");
        }
        useAuth.getState().setToken(newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const forceLogout = () => {
  AuthActions.logout();
  window.location.href = "/login";
};

export const createApiClient = (auth = true) => {
  const http = axios.create({
    baseURL: Config.apiBaseUrl,
    withCredentials: true,
  });

  http.interceptors.request.use(
    function (config: any) {
      const token: any = useAuth.getState().token;

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };

        // config.headers = {...config.headers, 'content-Type':'application/x-www-form-urlencoded'}
      }
      // console.log(config.headers, token);
      // console.log(config);
      return config;
    },
    function (error) {
      // Do something with request error
      return Promise.reject(error);
    },
  );

  http.interceptors.response.use(
    (request) => {
      return request;
    },
    async (err) => {
      const originalRequest = err.config;
      // A 401 on a request that carried a token means that token is no
      // longer valid — most commonly it simply expired mid-session (e.g. a
      // member takes a break partway through KYC/identity verification).
      // Requests sent without a token (e.g. a failed login attempt) are
      // excluded so a wrong-password error doesn't force a redirect — the
      // calling page handles that itself.
      const hadToken = Boolean(useAuth.getState().token);

      if (
        hadToken &&
        err.response &&
        err.response.status === 401 &&
        originalRequest &&
        !originalRequest._retriedAfterRefresh
      ) {
        originalRequest._retriedAfterRefresh = true;
        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return http(originalRequest);
        } catch (refreshError) {
          // The refresh token is also gone (expired, revoked, or this is a
          // genuinely stale session) — there's no way to recover silently,
          // so fall back to the previous behavior.
          forceLogout();
          return Promise.reject(refreshError);
        }
      }

      // Either this request already went through a refresh-and-retry and
      // still failed, or it wasn't eligible for one — nothing left to try.
      if (hadToken && err.response && err.response.status === 401) {
        forceLogout();
      }
      return Promise.reject(err);
    },
  );

  return http;
};
